import { queryKeyPrefix } from '@src/corelib/query/utils';
import { useMutation } from '@src/query';

import { inventoryKeyPrefix, salesKeyPrefix } from '../sales/useSaleMutations';


/**
 * M31 fulfillment mutation hooks (#491). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn` (the one place `queryClient` is injected).
 *
 * Mirrors `query/sales/useSaleMutations.js` (the invalidation-prefix helpers are
 * reused from there): one `query/<area>/use<Thing>Mutations.js` module re-exported
 * through the `@src/query` barrel.
 *
 * A fulfillment write touches THREE cache surfaces:
 *  - the shipping fulfillments cache (the sale-fulfillment list AND every
 *    fulfillment-detail query — a single 5-element prefix covers both, see
 *    `fulfillmentsKeyPrefix`),
 *  - the sales list/totals (buying a label / marking delivered flips the Sale's
 *    `shipment_status`),
 *  - the cross-resource inventory caches (parts / listings / dashboard).
 * Rate-shopping is the exception: it only flips the fulfillment to `rated`, so it
 * invalidates the fulfillments cache alone and returns the rates to the caller.
 */

/** Cache-key prefix covering BOTH the sale-fulfillment list query
 * (`/shipping/${sellerId}/fulfillments/`) and every fulfillment-detail query
 * (`/shipping/${sellerId}/fulfillments/${id}/`) — the 5-element shared prefix
 * (`urlToQueryKey` splits on `/`; both keys agree through `fulfillments`). */
function fulfillmentsKeyPrefix(sellerId) {
  return queryKeyPrefix('shipping', String(sellerId || ''), 'fulfillments');
}

/** Invalidate the fulfillments cache only (list + all details). */
function invalidateFulfillments(queryClient, sellerId) {
  queryClient.invalidateQueries({ queryKey: fulfillmentsKeyPrefix(sellerId) });
}

/** Invalidate every surface a fulfillment write touches: fulfillments + sales
 * list/totals + the cross-resource inventory caches. */
function invalidateFulfillmentWrite(queryClient, sellerId) {
  invalidateFulfillments(queryClient, sellerId);
  queryClient.invalidateQueries({ queryKey: salesKeyPrefix(sellerId) });
  for (const resource of ['parts', 'listings', 'dashboard']) {
    queryClient.invalidateQueries({ queryKey: inventoryKeyPrefix(sellerId, resource) });
  }
}


/**
 * Open a NON-CARRIER fulfillment (pickup / local_delivery). Variables:
 * `{ sellerId, saleId, method, location }` (`location` optional pickup-yard uuid).
 * POSTs the create endpoint → 201 FulfillmentDetail (pickup → `ready_for_pickup`,
 * local → `out_for_local_delivery`), then invalidates all write surfaces.
 */
function useCreateNonCarrierFulfillmentMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, saleId, method, location }) => {
      const payload = { sale: saleId, method };
      if (location) {
        payload.location = location;
      }
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/`,
        method: 'POST',
        payload,
      });
      invalidateFulfillmentWrite(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Open a CARRIER fulfillment (buy-label flow). Variables:
 * `{ sellerId, saleId, payload }` where `payload` is the ship-to + package body
 * (`to_name`, `to_street1`, …, `weight_oz` decimal string). POSTs the carrier
 * endpoint → 201 FulfillmentDetail (status `draft`), then invalidates all write
 * surfaces. 400 field errors (`to_*` / `weight_oz`) map back to the form.
 */
function useCreateCarrierFulfillmentMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, saleId, payload }) => {
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/carrier/`,
        method: 'POST',
        payload: { sale: saleId, ...payload },
      });
      invalidateFulfillmentWrite(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Edit the PARCEL of a not-yet-bought carrier fulfillment (#529). Variables:
 * `{ sellerId, fulfillmentId, payload }` where `payload` carries the package fields
 * ONLY (`weight_oz` decimal string + the three nullable dimensions) — the backend
 * 400s any other key rather than silently dropping it. PATCHes the detail endpoint →
 * FulfillmentDetail.
 *
 * A `rated` fulfillment comes back as `draft`: the quoted rates were priced against
 * the OLD parcel, so the caller must re-rate before it can buy. Invalidates the
 * fulfillments cache alone (the Sale's coarse shipment_status is unchanged —
 * draft and rated both summarise as not-shipped).
 */
function useUpdateParcelMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, fulfillmentId, payload }) => {
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/${fulfillmentId}/`,
        method: 'PATCH',
        payload,
      });
      invalidateFulfillments(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Rate-shop a carrier draft. Variables: `{ sellerId, fulfillmentId }`. POSTs the
 * rates endpoint (empty body) → `{ provider_shipment_id, rates: [...] }` returned
 * to the caller. Side effect: moves the fulfillment to `rated`, so this
 * invalidates the fulfillments cache (detail status flips) but NOT sales/inventory.
 */
function useRateFulfillmentMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, fulfillmentId }) => {
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/${fulfillmentId}/rates/`,
        method: 'POST',
      });
      invalidateFulfillments(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Buy a shipping label for a rated carrier fulfillment. Variables:
 * `{ sellerId, fulfillmentId, rateId }`. POSTs the buy-label endpoint →
 * FulfillmentDetail (status `purchased`; tracking + label populated), then
 * invalidates all write surfaces. 400 errors (`detail`) map back to a toast.
 */
function useBuyLabelMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, fulfillmentId, rateId }) => {
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/${fulfillmentId}/buy-label/`,
        method: 'POST',
        payload: { rate_id: rateId },
      });
      invalidateFulfillmentWrite(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Void a purchased carrier label (refund). Variables: `{ sellerId, fulfillmentId }`.
 * POSTs the void-label endpoint (empty body) → FulfillmentDetail (refunded), then
 * invalidates all write surfaces.
 */
function useVoidLabelMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, fulfillmentId }) => {
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/${fulfillmentId}/void-label/`,
        method: 'POST',
      });
      invalidateFulfillmentWrite(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


/**
 * Mark a pickup / local-delivery fulfillment delivered. Variables:
 * `{ sellerId, fulfillmentId, at }` (`at` optional ISO backdate; a future value or
 * a carrier fulfillment → 400). POSTs mark-delivered → FulfillmentDetail (status
 * `delivered`), then invalidates all write surfaces.
 */
function useMarkDeliveredMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, fulfillmentId, at }) => {
      const data = await apiClient.request({
        url: `/shipping/${sellerId}/fulfillments/${fulfillmentId}/mark-delivered/`,
        method: 'POST',
        payload: at ? { at } : {},
      });
      invalidateFulfillmentWrite(queryClient, sellerId);
      return data;
    },
    ...params,
  });
}


export {
  fulfillmentsKeyPrefix,
  useBuyLabelMutation,
  useCreateCarrierFulfillmentMutation,
  useCreateNonCarrierFulfillmentMutation,
  useMarkDeliveredMutation,
  useRateFulfillmentMutation,
  useUpdateParcelMutation,
  useVoidLabelMutation,
};
